import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import {
  CollectionSettings,
  ExtendedWarrantyShopMetafield,
  ExtendedWarrantyShopMetafieldStyles,
  SHOP_METAFIELD_KEY,
  SHOP_METAFIELD_NAMESPACE,
  SHOP_METAFIELD_STYLES_KEY,
} from "@redotech/redo-return-warranties-common/extended-warranty";
import { createContext, memo, ReactNode, useMemo } from "react";
import { useLocationManager } from "../../location";
import { ExtensionShopifyClientContext } from "../../shopify";
import { useExpandedWarrantyTreatment } from "../hooks/use-expanded-warranty-treatment";

export const EwSettingsContext = createContext<
  | {
      offeringConfig: ExtendedWarrantyShopMetafield;
      collectionSettings: CollectionSettings[];
      getWidgetConfig: () => ExtendedWarrantyShopMetafieldStyles;
    }
  | undefined
>({
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  offeringConfig: {} as ExtendedWarrantyShopMetafield,
  collectionSettings: [] as CollectionSettings[],
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  getWidgetConfig: () => ({}) as ExtendedWarrantyShopMetafieldStyles,
});

export const EwSettingsProvider = memo(function EwSettingsProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const shopifyClient = useRequiredContext(ExtensionShopifyClientContext);

  const locationManager = useLocationManager();

  const locationLoad = useLoad(
    async (signal) => {
      return locationManager.getDetailedLocation(signal);
    },
    [locationManager],
  );

  const expandedWarrantyTreatmentLoad = useExpandedWarrantyTreatment({
    currentUrl: new URL(location.href),
  });

  const offeringsConfigLoad = useLoad(async () => {
    const offeringConfigMetafield = await shopifyClient.getShopMetafield({
      key: SHOP_METAFIELD_KEY,
      namespace: SHOP_METAFIELD_NAMESPACE,
    });
    const offeringConfigValue = offeringConfigMetafield?.metafield?.value;
    if (!offeringConfigValue) {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return JSON.parse(offeringConfigValue) as ExtendedWarrantyShopMetafield;
  }, [shopifyClient]);

  const widgetConfigsLoad = useLoad(async () => {
    const widgetConfigsMetafield = await shopifyClient.getShopMetafield({
      key: SHOP_METAFIELD_STYLES_KEY,
      namespace: SHOP_METAFIELD_NAMESPACE,
    });
    const widgetConfigsValue = widgetConfigsMetafield?.metafield?.value;
    if (!widgetConfigsValue) {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const baseWidgetConfig = JSON.parse(
      widgetConfigsValue,
    ) as ExtendedWarrantyShopMetafieldStyles;
    if (expandedWarrantyTreatmentLoad.value) {
      return {
        ...baseWidgetConfig,
        ...expandedWarrantyTreatmentLoad.value.ewWidgetConfig,
        sideImage:
          expandedWarrantyTreatmentLoad.value.ewWidgetConfig.sideImage ??
          baseWidgetConfig?.sideImage,
        logo:
          expandedWarrantyTreatmentLoad.value.ewWidgetConfig.logo ??
          baseWidgetConfig?.logo,
      };
    }
    return baseWidgetConfig;
  }, [shopifyClient, expandedWarrantyTreatmentLoad.value]);

  const offeringConfigCollectionSettings = useMemo(() => {
    return offeringsConfigLoad.value?.collectionSettings;
  }, [offeringsConfigLoad.value]);

  // Eventually this will be keyed to a widget type
  const getWidgetConfig = () => {
    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      widgetConfigsLoad.value ?? ({} as ExtendedWarrantyShopMetafieldStyles)
    );
  };

  const expandedWarrantiesEnabled = useMemo(() => {
    return (
      offeringsConfigLoad.value?.enabled ||
      expandedWarrantyTreatmentLoad.value?.settings.expandedWarrantiesEnabled
    );
  }, [offeringsConfigLoad.value, expandedWarrantyTreatmentLoad.value]);

  if (locationLoad.pending || locationLoad.value === undefined) {
    return null;
  }

  const { country: ipCountry, region } = locationLoad.value;
  const shopifyCountry = globalThis.Shopify?.country;

  // We can't sell extended warranties outside of the US
  if (
    (shopifyCountry && shopifyCountry.toLowerCase() !== "us") ||
    (ipCountry && ipCountry.toLowerCase() !== "us")
  ) {
    return null;
  }

  // We can't sell extended warranties in Maine
  if (region?.toLowerCase() === "me" || region?.toLowerCase() === "maine") {
    return null;
  }

  if (
    offeringsConfigLoad.value === undefined ||
    widgetConfigsLoad.value === undefined ||
    !expandedWarrantiesEnabled ||
    offeringConfigCollectionSettings === undefined ||
    (expandedWarrantyTreatmentLoad.value &&
      !expandedWarrantyTreatmentLoad.value.settings.expandedWarrantiesEnabled)
  ) {
    return null;
  }

  return (
    <EwSettingsContext.Provider
      value={{
        offeringConfig: offeringsConfigLoad.value,
        collectionSettings: offeringConfigCollectionSettings,
        getWidgetConfig,
      }}
    >
      {children}
    </EwSettingsContext.Provider>
  );
});
